import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ContentProvider } from "@/context/ContentContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { FloatingContact } from "@/components/FloatingContact";
import { Loader } from "@/components/Loader";
import { Cursor } from "@/components/Cursor";
import { ScrollBeam } from "@/components/ScrollBeam";
import Home from "@/pages/Home";
import BecomeCarrier from "@/pages/BecomeCarrier";
import Contact from "@/pages/Contact";
import Legal from "@/pages/Legal";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";

const HashScroll = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const t = setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) {
          if (window.__lenis) window.__lenis.scrollTo(el, { offset: -90 });
          else el.scrollIntoView({ behavior: "smooth" });
        }
      }, 150);
      return () => clearTimeout(t);
    }
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
};

const Shell = ({ children }) => {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  return (
    <>
      {!isAdmin && <ScrollBeam />}
      {!isAdmin && <Navbar />}
      {children}
      {!isAdmin && (
        <>
          <FloatingContact />
          <Footer />
        </>
      )}
    </>
  );
};

function App() {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 1900);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const lenis = new Lenis({ lerp: 0.09 });
    window.__lenis = lenis;
    lenis.on("scroll", ({ velocity }) => {
      const v = Math.max(-28, Math.min(28, velocity || 0));
      document.documentElement.style.setProperty("--vel", v.toFixed(2));
    });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <div className="App bg-ink min-h-screen text-white">
      <AnimatePresence>{loading && <Loader />}</AnimatePresence>
      <AuthProvider>
        <ContentProvider>
          <BrowserRouter>
            <HashScroll />
            <Shell>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/become-a-carrier" element={<BecomeCarrier />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/privacy" element={<Legal type="privacy" />} />
                <Route path="/terms" element={<Legal type="terms" />} />
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="*" element={<Home />} />
              </Routes>
            </Shell>
          </BrowserRouter>
        </ContentProvider>
      </AuthProvider>
      <Cursor />
      <Toaster theme="dark" position="top-right" toastOptions={{ style: { background: "#1c1c1c", border: "1px solid rgba(212,175,55,0.35)", color: "#fff" } }} />
    </div>
  );
}

export default App;
